import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import CaseIcon from '@crm/shared/assets/icons/case.svg?react';
import HelmetIcon from '@crm/shared/assets/icons/helmet.svg?react';
import { UiOnboard } from '@shared/ui/UiOnboard';
import { CustomModal, Flex, UiButton, UiText } from '@shared/ui';

import styles from './SmetterModuleSwitcher.module.css';
import { getCrmAccessState } from '../model/moduleSwitch';
import { useModuleSwitch } from '../model/useModuleSwitch';

import type { TConstructionModuleSwitcherProps } from '../model/moduleSwitch';

export const ConstructionModuleSwitcher = (props: TConstructionModuleSwitcherProps) => {
    const { t } = useTranslation();
    const { isSwitching, switchModule } = useModuleSwitch(props.source);
    const [isTariffModalOpen, setIsTariffModalOpen] = useState(false);

    const crmAccessState = getCrmAccessState(props);

    if (crmAccessState === 'hidden') return null;

    const constructionLabel = t('crm.smetterModuleSwitcher.construction');
    const salesLabel = t('crm.smetterModuleSwitcher.sales');
    const isTariffRestricted = crmAccessState === 'tariff-restricted';

    const closeTariffModal = () => setIsTariffModalOpen(false);
    const changeTariff = () => {
        $.modalable('open', { url: props.tariffModalUrl, content: props.tariffModalContent });
        closeTariffModal();
    };

    return (
        <>
            <CustomModal isOpen={isTariffModalOpen} onHideModal={closeTariffModal}>
                <CustomModal.Header>
                    {t('crm.smetterModuleSwitcher.tariffRestriction.title')}
                </CustomModal.Header>
                <CustomModal.Body width="546px">
                    <Flex direction="column" gap={24}>
                        <UiText classes={styles.text}>
                            {t('crm.smetterModuleSwitcher.tariffRestriction.description')}
                        </UiText>
                        <div className={styles.video}>
                            <iframe
                                title={t('crm.smetterModuleSwitcher.tariffRestriction.videoTitle')}
                                src="https://rutube.ru/play/embed/a1646bb72bb201cee18b1862456c86db/?r=wd"
                            />
                        </div>
                    </Flex>
                </CustomModal.Body>
                <CustomModal.Footer classes={styles.footer}>
                    {IS_MY_COMPANY ? (
                        <>
                            <Flex gap={8}>
                                <UiButton
                                    type="button"
                                    aria-haspopup="dialog"
                                    onClick={changeTariff}
                                    btnText={t(
                                        'PresentationModal.return.CustomModal.Footer.changeTariff.btnText'
                                    )}
                                />
                                <UiButton
                                    btnColor="secondary"
                                    btnText={t(
                                        'crm.smetterModuleSwitcher.tariffRestriction.helpCenter'
                                    )}
                                    onClick={() =>
                                        window.open(
                                            'https://help.smetter.ru/start_crm',
                                            '_blank',
                                            'noopener,noreferrer'
                                        )
                                    }
                                />
                            </Flex>
                            <a href="//smetter.ru/tariffs" target="_blank" rel="noreferrer">
                                {t('PresentationModal.return.CustomModal.Footer.a')}
                            </a>
                        </>
                    ) : (
                        <>
                            <UiText tag="p">{t('useDashboard.return.changeTariff.content')}</UiText>
                            <UiButton
                                btnColor="secondary"
                                btnText={t(
                                    'crm.smetterModuleSwitcher.tariffRestriction.helpCenter'
                                )}
                                onClick={() =>
                                    window.open(
                                        'https://help.smetter.ru/start_crm',
                                        '_blank',
                                        'noopener,noreferrer'
                                    )
                                }
                            />
                        </>
                    )}
                </CustomModal.Footer>
            </CustomModal>
            <div className={styles.constructionRoot}>
                <UiOnboard
                    title={t('crm.smetterModuleSwitcher.onboarding.title')}
                    subTitle={t('crm.smetterModuleSwitcher.onboarding.description')}
                    confirmText={t('button.got_it.text')}
                    highlightClasses={styles.constructionOnboardHighlight}
                    localStorageKey="crm_switch"
                    elementId="crm_switch"
                    elementContent={
                        <>
                            <CaseIcon aria-hidden="true" className={styles.constructionIcon} />
                            <span>{salesLabel}</span>
                        </>
                    }
                />
                <div className={styles.constructionSwitcher}>
                    <span
                        aria-hidden="true"
                        className={`${styles.constructionIndicator} ${
                            isSwitching ? styles.salesActive : ''
                        }`}
                    />
                    <button
                        type="button"
                        title={constructionLabel}
                        aria-label={constructionLabel}
                        aria-disabled="true"
                        className={`${styles.constructionButton} ${
                            !isSwitching ? styles.constructionButtonActive : ''
                        }`}
                    >
                        <HelmetIcon aria-hidden="true" className={styles.constructionIcon} />
                        <span className={styles.constructionLabel}>{constructionLabel}</span>
                    </button>
                    <button
                        type="button"
                        id="crm_switch"
                        title={salesLabel}
                        aria-label={salesLabel}
                        aria-disabled={isSwitching}
                        aria-haspopup={isTariffRestricted ? 'dialog' : undefined}
                        className={`${styles.constructionButton} ${
                            isSwitching ? styles.constructionButtonActive : ''
                        }`}
                        onClick={
                            isTariffRestricted ? () => setIsTariffModalOpen(true) : switchModule
                        }
                    >
                        <CaseIcon aria-hidden="true" className={styles.constructionIcon} />
                        <span className={styles.constructionLabel}>{salesLabel}</span>
                    </button>
                </div>
            </div>
        </>
    );
};
